import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { timeout, restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import type { Registry as Services } from '@ember/service';
import { scheduleTask } from 'ember-lifeline';
import BaseController from 'uplisting-frontend/pods/base/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { searchOptions } from 'uplisting-frontend/utils/search';
import { KnowledgeBaseFileStatus } from 'uplisting-frontend/models/ai-knowledge-base-file';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import {
  type Filter,
  type KnowledgeCenterFilter,
} from 'uplisting-frontend/utils/filters/types';

export default class AiMessagingContentFilesController extends BaseController {
  @service sentry!: Services['sentry'];
  @service('filters') filtersService!: Services['filters'];

  queryParams = ['propertyIds', 'highlightFileId'];

  @tracked files: ModelRegistry['ai-knowledge-base-file'][] = [];
  @tracked properties: ModelRegistry['property'][] = [];
  @tracked searchQuery = '';
  @tracked propertyIds: string[] = [];
  @tracked highlightFileId?: string;
  @tracked activeTab: 'global' | 'property' = 'global';
  @tracked isFileUploadPanelOpen = false;
  @tracked isDeleteModalOpen = false;
  @tracked fileToDelete?: ModelRegistry['ai-knowledge-base-file'];
  @tracked deleteQaPairs = false;

  POLL_INTERVAL = 5000;

  get hasProcessingFiles(): boolean {
    return this.files.some(
      (file) => file.status === KnowledgeBaseFileStatus.processing,
    );
  }

  pollProcessingFiles = restartableTask(async () => {
    while (this.hasProcessingFiles) {
      await timeout(this.POLL_INTERVAL);
      await this.store.findAll('ai-knowledge-base-file');
    }
  });

  @cached
  get propertyFilter(): KnowledgeCenterFilter {
    return this.filtersService.createFilterFor(
      FilterId.property,
      this.propertyIds,
    ) as KnowledgeCenterFilter;
  }

  @cached
  get displayedFiles(): ModelRegistry['ai-knowledge-base-file'][] {
    let files = this.files.slice();

    if (this.activeTab === 'global') {
      files = files.filter((file) => !file.property?.id);
    } else if (this.propertyIds.length > 0) {
      files = files.filter(
        (file) =>
          !!file.property?.id && this.propertyIds.includes(file.property.id),
      );
    } else {
      files = files.filter((file) => !!file.property?.id);
    }

    return searchOptions(files, this.searchQuery, ['fileName']);
  }

  scrollToHighlightedFile(): void {
    scheduleTask(this, 'render', () => {
      const row = window.document.querySelector(
        `[data-file-id="${this.highlightFileId}"]`,
      );

      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  @action
  handleSearchInput(value: string): void {
    this.searchQuery = value;
  }

  @action
  handleTabChange(tab: 'global' | 'property'): void {
    this.activeTab = tab;
    this.propertyIds = [];
  }

  @action
  handleFilterUpdate(filter: Filter): void {
    this.propertyIds = filter.toQuery();
    this.highlightFileId = undefined;
  }

  @action
  openFileUploadPanel(): void {
    this.isFileUploadPanelOpen = true;
  }

  @action
  closeFileUploadPanel(): void {
    this.isFileUploadPanelOpen = false;
  }

  @action
  handleFileUploaded(): void {
    this.store.findAll('ai-knowledge-base-file');
    this.pollProcessingFiles.perform();
  }

  @action
  confirmDeleteFile(file: ModelRegistry['ai-knowledge-base-file']): void {
    this.fileToDelete = file;
    this.isDeleteModalOpen = true;
  }

  @action
  cancelDeleteFile(): void {
    this.fileToDelete = undefined;
    this.isDeleteModalOpen = false;
    this.deleteQaPairs = false;
  }

  @action
  toggleDeleteQaPairs(): void {
    this.deleteQaPairs = !this.deleteQaPairs;
  }

  @action
  @waitFor
  async handleDeleteFile(): Promise<void> {
    if (!this.fileToDelete) return;

    try {
      await this.fileToDelete.destroyRecord({
        adapterOptions: { delete_qa_pairs: this.deleteQaPairs },
      });
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.fileToDelete = undefined;
      this.isDeleteModalOpen = false;
      this.deleteQaPairs = false;
    }
  }
}
